import { apiClient } from '../client';

export interface DeletionRequestUserSnapshot {
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  school_id?: {
    _id: string;
    school_name: string;
  };
  campus_id?: {
    _id: string;
    campus_name: string;
  };
}

export interface DeletionRequest {
  _id: string;
  user_id: {
    _id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
    is_delete?: boolean;
  } | string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'cancelled';
  user_snapshot: DeletionRequestUserSnapshot;
  reviewed_by?: {
    _id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  reviewed_at?: string;
  resolution_notes?: string;
  completed_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeletionRequestStats {
  pending: number;
  approved: number;
  rejected: number;
  completed: number;
  cancelled: number;
  total: number;
}

export interface DeletionRequestFilters {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  school_id?: string;
  campus_id?: string;
}

export interface DeletionRequestsResponse {
  success: boolean;
  data: DeletionRequest[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface DeletionRequestStatsResponse {
  success: boolean;
  data: DeletionRequestStats;
}

export const deletionRequestsService = {
  // Get all deletion requests with filters
  getDeletionRequests: async (filters?: DeletionRequestFilters): Promise<{
    requests: DeletionRequest[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const params = new URLSearchParams();

    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.school_id) params.append('school_id', filters.school_id);
    if (filters?.campus_id) params.append('campus_id', filters.campus_id);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get<DeletionRequestsResponse>(
      `/api/admin/deletion-requests?${params.toString()}`
    );

    return {
      requests: response.data.data || [],
      pagination: response.data.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
      },
    };
  },

  // Get deletion request statistics
  getStats: async (filters?: { school_id?: string; campus_id?: string }): Promise<DeletionRequestStats> => {
    const params = new URLSearchParams();

    if (filters?.school_id) params.append('school_id', filters.school_id);
    if (filters?.campus_id) params.append('campus_id', filters.campus_id);

    const response = await apiClient.get<DeletionRequestStatsResponse>(
      `/api/admin/deletion-requests/stats?${params.toString()}`
    );

    return response.data.data;
  },

  // Get single deletion request details
  getDetails: async (requestId: string): Promise<DeletionRequest> => {
    const response = await apiClient.get(`/api/admin/deletion-requests/${requestId}`);
    return response.data.data;
  },

  // Approve a deletion request
  approve: async (requestId: string, resolutionNotes?: string): Promise<DeletionRequest> => {
    const response = await apiClient.post(`/api/admin/deletion-requests/${requestId}/approve`, {
      resolution_notes: resolutionNotes,
    });
    return response.data.data;
  },

  // Reject a deletion request
  reject: async (requestId: string, resolutionNotes: string): Promise<DeletionRequest> => {
    const response = await apiClient.post(`/api/admin/deletion-requests/${requestId}/reject`, {
      resolution_notes: resolutionNotes,
    });
    return response.data.data;
  },

  // Complete a deletion request (delete the user account)
  complete: async (requestId: string, resolutionNotes?: string): Promise<DeletionRequest> => {
    const response = await apiClient.post(`/api/admin/deletion-requests/${requestId}/complete`, {
      resolution_notes: resolutionNotes,
    });
    return response.data.data;
  },
};

export { deletionRequestsService as deletionRequestsAPI };
